import {
  ApiResponse,
  IDanhMucToaXeResponseBase,
  IDonViPdfExtra,
  PaginationResponseBase,
} from '@/core/interfaces/ApiResponses';
import { apiServiceInstance } from '@/core/helpers/utils';

export default class DanhMucToaXeService {
  static async listAll(
    searchTerm = '',
    per_page = 200
  ): Promise<{
    items: IDanhMucToaXeResponseBase[];
    extra?: IDonViPdfExtra;
  }> {
    const allItems: IDanhMucToaXeResponseBase[] = [];
    let page = 1;
    let total = Infinity;
    let extra: IDonViPdfExtra | undefined;

    while (allItems.length < total) {
      const { data: response } = await DanhMucToaXeService.list(
        page,
        per_page,
        searchTerm
      );
      const {
        data: { data, total: totalItems },
      } = response;

      if (page === 1) {
        extra = response.extra as IDonViPdfExtra | undefined;
      }

      total = totalItems;
      allItems.push(...data);

      if (!data.length) break;
      page += 1;
    }

    return { items: allItems, extra };
  }

  static list(
    current_page = 1,
    per_page = 200,
    searchTerm = ''
  ): Promise<{
    data: ApiResponse<PaginationResponseBase<IDanhMucToaXeResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      '/api/v1/danh-muc-toa-xe/danh-sach',
      {
        params: {
          page: current_page,
          ...(searchTerm && {
            s: searchTerm,
          }),
        },
        headers: {
          perpage: per_page,
        },
      }
    );
  }

  static get(id: number): Promise<{
    data: ApiResponse<IDanhMucToaXeResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/danh-muc-toa-xe/chi-tiet/${id}`
    );
  }

  static generateMaDanhMuc(params?: {
    id_cha?: number;
    ten_danh_muc?: string;
    id?: number;
  }): Promise<{
    data: ApiResponse<{
      mode: string;
      ma_danh_muc: string;
      ten_danh_muc: string | null;
      id_cha: number;
      cap_so: number;
      parent: IDanhMucToaXeResponseBase | null;
    }>;
  }> {
    const queryParams: Record<string, string | number> = {};

    if (typeof params?.id_cha === 'number') {
      queryParams.id_cha = params.id_cha;
    }

    if (params?.ten_danh_muc?.trim()) {
      queryParams.ten_danh_muc = params.ten_danh_muc.trim();
    }

    if (typeof params?.id === 'number' && params.id > 0) {
      queryParams.id = params.id;
    }

    return apiServiceInstance().vueInstance.axios.get(
      '/api/v1/danh-muc-toa-xe/generate-ma-danh-muc',
      {
        params: Object.keys(queryParams).length ? queryParams : undefined,
      }
    );
  }

  static create(
    ten_danh_muc: string,
    mo_ta: string,
    id_cha: number
  ): Promise<{
    data: ApiResponse<IDanhMucToaXeResponseBase>;
  }> {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';
    const bodyFormData = new FormData();

    bodyFormData.append('danhMucToaXeInstance[ten_danh_muc]', ten_danh_muc);
    bodyFormData.append('danhMucToaXeInstance[mo_ta]', mo_ta);
    bodyFormData.append('danhMucToaXeInstance[id_cha]', id_cha.toString());

    return apiServiceInstance().vueInstance.axios.post(
      '/api/v1/danh-muc-toa-xe/tao-moi',
      bodyFormData
    );
  }

  static update(
    id: number,
    ten_danh_muc: string,
    mo_ta: string,
    id_cha: number
  ): Promise<{
    data: ApiResponse<IDanhMucToaXeResponseBase>;
  }> {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';
    const bodyFormData = new FormData();

    bodyFormData.append('danhMucToaXeInstance[ten_danh_muc]', ten_danh_muc);
    bodyFormData.append('danhMucToaXeInstance[mo_ta]', mo_ta);
    bodyFormData.append('danhMucToaXeInstance[id_cha]', id_cha.toString());
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/danh-muc-toa-xe/cap-nhat/${id}`,
      bodyFormData
    );
  }

  static assignToaXeItems(id: number, toaXeIds: number[]) {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';
    const bodyFormData = new FormData();

    toaXeIds.forEach((id) => {
      bodyFormData.append('toaXeInstance[]', id.toString());
    });

    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/danh-muc-toa-xe/toa-xe/${id}`,
      bodyFormData
    );
  }

  static addVatTuVaoDinhMuc(
    id: number,
    items: {
      id: number;
      so_luong: number;
    }[]
  ) {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      'Content-Type'
    ] = 'multipart/form-data';
    const bodyFormData = new FormData();

    items.forEach((item) => {
      bodyFormData.append(
        `dinhMucCoDinh[${item.id}]`,
        item.so_luong.toString()
      );
    });

    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/danh-muc-toa-xe/dinh-muc-co-dinh/${id}`,
      bodyFormData
    );
  }

  static delete(id: number) {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/danh-muc-toa-xe/xoa/${id}`
    );
  }

  static detachToaXe({
    id_toa_xe,
    id_dm_toa_xe,
  }: {
    id_toa_xe: number;
    id_dm_toa_xe: number;
  }) {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/danh-muc-toa-xe/${id_dm_toa_xe}/toa-xe/${id_toa_xe}/detach`
    );
  }
}
